import { Injectable, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  OrganizationContextItem,
  OwnerPortalService,
  OwnerEntityContext,
  WorkspaceBranding,
} from './owner-portal.service';

@Injectable({ providedIn: 'root' })
export class OwnerWorkspaceService {
  private readonly api = inject(OwnerPortalService);
  private readonly workspace = signal<OrganizationContextItem | null>(null);
  private workspaces: OrganizationContextItem[] | null = null;
  private readonly branding = signal<WorkspaceBranding | null>(null);
  private entities: OwnerEntityContext[] = [];
  private entityId: string | null = null;
  private entityRequired = false;

  async resolveWorkspace(): Promise<OrganizationContextItem | null> {
    // Revalidate on every call, but publish the result atomically. Child pages also
    // resolve their context during initialization; clearing it synchronously here
    // changes the shell's navigation in the middle of Angular's render pass.
    try {
      const context = await firstValueFrom(this.api.getOrganizationContext());
      this.workspaces = context.organizations ?? [];
      const requestedOrgId = this.queryParam('orgId') || this.storedOrgId();
      const ownerWorkspaces = this.workspaces.filter((org) => this.isOwnerPortal(org));
      const selected = requestedOrgId
        ? ownerWorkspaces.find((org) => org.orgId === requestedOrgId) ?? null
        : ownerWorkspaces.find((org) => org.orgId === context.defaultOrgId) ?? ownerWorkspaces[0] ?? null;

      if (!selected) {
        this.resetResolvedContext();
        return null;
      }

      const entityRequired = selected.orgRole === 'LANDLORD_PORTAL' || selected.portalMode === 'OWNER_PORTAL';
      const entities = entityRequired
        ? await firstValueFrom(this.api.getCurrentOwnerEntities(selected.orgId))
        : [];
      const requestedEntityId = this.queryParam('ownerEntityId') || this.storedEntityId(selected.orgId);
      // The login already chose the workspace, so don't ask again: open the entity used last, otherwise the
      // first that owns property (the server sorts them by name). The header switches between them.
      const entityId = entities.find((entity) => entity.id === requestedEntityId)?.id
        ?? (entities.find((entity) => entity.propertyIds.length) ?? entities[0])?.id
        ?? null;
      const propertyIds = entities.find((entity) => entity.id === entityId)?.propertyIds ?? [];
      this.api.clearOwnerEntityContext();
      if (entityRequired) {
        this.api.setOwnerEntityContext(selected.orgId, entityId, propertyIds);
      }
      this.entities = entities;
      this.entityId = entityId;
      this.entityRequired = entityRequired;
      this.workspace.set(entityRequired ? { ...selected, assignedPropertyIds: propertyIds } : selected);
      this.rememberOrg(selected.orgId);
      await this.loadBranding(selected.orgId);
      return this.workspace();
    } catch (error) {
      this.resetResolvedContext();
      throw error;
    }
  }

  private resetResolvedContext(): void {
    this.workspace.set(null);
    this.branding.set(null);
    this.entities = [];
    this.entityId = null;
    this.entityRequired = false;
    this.api.clearOwnerEntityContext();
  }

  async workspaceOptions(): Promise<OrganizationContextItem[]> {
    if (this.workspaces) {
      return this.workspaces;
    }

    const context = await firstValueFrom(this.api.getOrganizationContext());
    this.workspaces = context.organizations ?? [];
    return this.workspaces;
  }

  currentWorkspace(): OrganizationContextItem | null {
    return this.workspace();
  }

  currentBranding(): WorkspaceBranding | null {
    return this.branding();
  }

  ownerEntities(): OwnerEntityContext[] { return this.entities; }
  selectedOwnerEntityId(): string | null { return this.entityId; }
  requiresOwnerEntity(): boolean { return this.entityRequired; }

  async selectOwnerEntity(entityId: string): Promise<void> {
    const workspace = this.workspace();
    if (!workspace || !this.entities.some((entity) => entity.id === entityId)) {
      throw new Error('Owner entity is not available. Refresh your access.');
    }
    this.api.setOwnerEntityContext(workspace.orgId, entityId,
      this.entities.find((entity) => entity.id === entityId)?.propertyIds ?? []);
    try {
      await firstValueFrom(this.api.getCurrentOwner(workspace.orgId));
    } catch (error) {
      this.api.setOwnerEntityContext(workspace.orgId, this.entityId,
        this.entities.find((entity) => entity.id === this.entityId)?.propertyIds ?? []);
      throw error;
    }
    this.entityId = entityId;
    try { window.sessionStorage.setItem(`selectedOwnerEntity:${workspace.orgId}`, entityId); } catch { /* optional persistence */ }
    // Reload destroys subscriptions and cached views from the previous entity.
    const url = new URL('/dashboard', window.location.origin);
    url.searchParams.set('orgId', workspace.orgId);
    url.searchParams.set('ownerEntityId', entityId);
    window.location.assign(url.toString());
  }

  private storedEntityId(orgId: string): string | null {
    try { return window.sessionStorage.getItem(`selectedOwnerEntity:${orgId}`); } catch { return null; }
  }

  async signOut(): Promise<void> {
    await firstValueFrom(this.api.logout()).catch(() => undefined);
    this.clearWorkspace();
    window.location.href = `${environment.ownerAppUrl}/login`;
  }

  switchWorkspace(): void {
    this.clearWorkspace();
    window.location.href = `${environment.publicAppUrl}/select-workspace`;
  }

  switchToWorkspace(organization: OrganizationContextItem): void {
    this.clearWorkspace();
    if (this.isOwnerPortalMode(organization)) {
      this.rememberOrg(organization.orgId);
    }
    window.location.href = this.workspaceDashboardUrl(organization);
  }

  // Every caller leaves the page next, so only the stored choice is forgotten. Clearing the live context
  // here would make the current page render "Owner portal unavailable" until the browser navigates away.
  private clearWorkspace(): void {
    const orgId = this.workspace()?.orgId;
    try {
      if (orgId) window.sessionStorage.removeItem(`selectedOwnerEntity:${orgId}`);
      window.sessionStorage.removeItem('selectedOwnerOrgId');
    } catch {
      // Storage is best-effort only.
    }
  }

  private async loadBranding(orgId: string): Promise<void> {
    this.branding.set(await firstValueFrom(this.api.getBranding(orgId)).catch(() => null));
    this.applyBranding(this.branding());
  }

  private applyBranding(branding: WorkspaceBranding | null): void {
    if (typeof document === 'undefined') {
      return;
    }

    const primary = this.validColor(branding?.primaryColor) || '#2FC1F1';
    const accent = this.validColor(branding?.accentColor) || primary;
    document.documentElement.style.setProperty('--workspace-primary', primary);
    document.documentElement.style.setProperty('--workspace-accent', accent);
    document.documentElement.style.setProperty('--workspace-secondary', accent);
  }

  private validColor(value: string | null | undefined): string | null {
    const color = value?.trim();
    return color && /^#[0-9A-Fa-f]{6}$/.test(color) ? color : null;
  }

  private isOwnerPortal(organization: OrganizationContextItem): boolean {
    const role = String(organization.orgRole ?? '').toUpperCase();
    const portalMode = String(organization.portalMode ?? '').toUpperCase();
    const orgType = String(organization.orgType ?? '').toUpperCase();
    return organization.owner === true
      || role === 'OWNER'
      || role === 'LANDLORD_PORTAL'
      || portalMode === 'OWNER_PORTAL'
      || (portalMode === 'LANDLORD_SELF' && orgType === 'LANDLORD_SELF');
  }

  private workspaceDashboardUrl(organization: OrganizationContextItem): string {
    const portalMode = String(organization.portalMode ?? '').toUpperCase();
    const landingPath = this.safeLandingPath(organization.landingPath);
    const baseUrl = portalMode === 'TENANT'
      ? environment.tenantAppUrl
      : this.isOwnerPortalMode(organization)
        ? environment.ownerAppUrl
        : environment.backofficeAppUrl;
    const url = new URL(`${this.portalBaseUrl(baseUrl, organization.portalSlug)}${landingPath}`);
    url.searchParams.set('orgId', organization.orgId);
    return url.toString();
  }

  private isOwnerPortalMode(organization: OrganizationContextItem): boolean {
    return String(organization.portalMode ?? '').toUpperCase() === 'OWNER_PORTAL';
  }

  private safeLandingPath(path?: string | null): string {
    const normalized = path?.trim();
    return normalized?.startsWith('/') ? normalized : '/dashboard';
  }

  private portalBaseUrl(baseUrl: string, portalSlug?: string | null): string {
    const slug = portalSlug?.trim().toLowerCase();
    if (!slug) {
      return baseUrl;
    }

    try {
      const url = new URL(baseUrl);
      if (url.hostname === 'localhost' || /^\d{1,3}(\.\d{1,3}){3}$/.test(url.hostname)) {
        return url.origin;
      }

      if (!url.hostname.startsWith(`${slug}.`)) {
        url.hostname = `${slug}.${url.hostname}`;
      }

      return url.origin;
    } catch {
      return baseUrl;
    }
  }

  private queryParam(key: string): string | null {
    if (typeof window === 'undefined') {
      return null;
    }
    return new URLSearchParams(window.location.search).get(key);
  }

  private storedOrgId(): string | null {
    try {
      return window.sessionStorage.getItem('selectedOwnerOrgId')?.trim() || null;
    } catch {
      return null;
    }
  }

  private rememberOrg(orgId: string): void {
    try {
      window.sessionStorage.setItem('selectedOwnerOrgId', orgId);
    } catch {
      // Storage is best-effort only.
    }
  }
}
